/**
 * Social Proof Agent
 * Aggregates and displays social signals like reviews, shares, and community activity
 */

interface SocialSignal {
  type: 'review' | 'share' | 'visit' | 'purchase' | 'favorite' | 'mention';
  timestamp: Date;
  userId?: string;
  content?: string;
  platform?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
}

interface SocialProofSummary {
  totalReviews: number;
  averageRating: number;
  recentActivity: number;
  popularityScore: number;
  trendingStatus: 'hot' | 'rising' | 'stable' | 'new';
  socialMentions: number;
  communityEndorsements: number;
}

interface TrendingItem {
  id: string;
  name: string;
  type: 'farm' | 'product' | 'category';
  score: number;
  signals: SocialSignal[];
  momentum: 'accelerating' | 'steady' | 'slowing';
}

class SocialProofAgent {
  /**
   * Calculate social proof score for an entity
   */
  calculateSocialProofScore(signals: SocialSignal[]): number {
    const weights = {
      review: 10,
      share: 5,
      visit: 1,
      purchase: 8,
      favorite: 6,
      mention: 3,
    };

    const recencyMultiplier = (timestamp: Date): number => {
      const daysAgo = (Date.now() - new Date(timestamp).getTime()) / (1000 * 60 * 60 * 24);
      if (daysAgo < 1) return 2;
      if (daysAgo < 7) return 1.5;
      if (daysAgo < 30) return 1;
      return 0.5;
    };

    const sentimentMultiplier = (sentiment?: string): number => {
      if (sentiment === 'positive') return 1.2;
      if (sentiment === 'negative') return 0.5;
      return 1;
    };

    let totalScore = 0;
    signals.forEach(signal => {
      const baseScore = weights[signal.type] || 1;
      const recency = recencyMultiplier(signal.timestamp);
      const sentiment = sentimentMultiplier(signal.sentiment);
      totalScore += baseScore * recency * sentiment;
    });

    return Math.round(totalScore);
  }

  /**
   * Generate social proof summary
   */
  generateSocialProofSummary(
    signals: SocialSignal[],
    reviews: { rating: number }[]
  ): SocialProofSummary {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const recentSignals = signals.filter(s => new Date(s.timestamp) > weekAgo);
    const socialMentions = signals.filter(s => s.type === 'mention' || s.type === 'share').length;

    const averageRating = reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

    const popularityScore = this.calculateSocialProofScore(signals);

    let trendingStatus: SocialProofSummary['trendingStatus'] = 'stable';
    if (signals.length < 5) trendingStatus = 'new';
    else if (recentSignals.length > signals.length * 0.3) trendingStatus = 'hot';
    else if (recentSignals.length > signals.length * 0.15) trendingStatus = 'rising';

    return {
      totalReviews: reviews.length,
      averageRating: Math.round(averageRating * 10) / 10,
      recentActivity: recentSignals.length,
      popularityScore,
      trendingStatus,
      socialMentions,
      communityEndorsements: signals.filter(s =>
        s.type === 'favorite' || (s.type === 'review' && s.sentiment === 'positive')
      ).length,
    };
  }

  /**
   * Get trending items
   */
  getTrendingItems(
    items: { id: string; name: string; type: 'farm' | 'product' | 'category'; signals: SocialSignal[] }[]
  ): TrendingItem[] {
    const trending = items.map(item => {
      const score = this.calculateSocialProofScore(item.signals);
      const recentSignals = item.signals.filter(s => {
        const daysAgo = (Date.now() - new Date(s.timestamp).getTime()) / (1000 * 60 * 60 * 24);
        return daysAgo < 7;
      });

      const olderSignals = item.signals.filter(s => {
        const daysAgo = (Date.now() - new Date(s.timestamp).getTime()) / (1000 * 60 * 60 * 24);
        return daysAgo >= 7 && daysAgo < 14;
      });

      let momentum: TrendingItem['momentum'] = 'steady';
      if (recentSignals.length > olderSignals.length * 1.5) momentum = 'accelerating';
      else if (recentSignals.length < olderSignals.length * 0.5) momentum = 'slowing';

      return {
        id: item.id,
        name: item.name,
        type: item.type,
        score,
        signals: item.signals,
        momentum,
      };
    });

    return trending.sort((a, b) => b.score - a.score);
  }

  /**
   * Generate social proof badges
   */
  generateBadges(summary: SocialProofSummary): { label: string; icon: string; color: string }[] {
    const badges: { label: string; icon: string; color: string }[] = [];

    if (summary.trendingStatus === 'hot') {
      badges.push({ label: 'Trending Now', icon: '🔥', color: '#FF5722' });
    }

    if (summary.averageRating >= 4.5 && summary.totalReviews >= 10) {
      badges.push({ label: 'Highly Rated', icon: '⭐', color: '#FFC107' });
    }

    if (summary.communityEndorsements >= 20) {
      badges.push({ label: 'Community Favorite', icon: '❤️', color: '#E91E63' });
    }

    if (summary.totalReviews >= 50) {
      badges.push({ label: 'Well Reviewed', icon: '✓', color: '#4CAF50' });
    }

    if (summary.recentActivity >= 10) {
      badges.push({ label: 'Active This Week', icon: '📈', color: '#2196F3' });
    }

    return badges;
  }

  /**
   * Generate activity feed
   */
  generateActivityFeed(
    signals: SocialSignal[],
    limit: number = 10
  ): { message: string; time: string; type: string }[] {
    const sortedSignals = [...signals].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return sortedSignals.slice(0, limit).map(signal => {
      const timeAgo = this.getTimeAgo(signal.timestamp);

      let message = '';
      switch (signal.type) {
        case 'review':
          message = `Someone left a ${signal.sentiment || 'new'} review`;
          break;
        case 'share':
          message = `Shared on ${signal.platform || 'social media'}`;
          break;
        case 'visit':
          message = 'Someone visited this page';
          break;
        case 'purchase':
          message = 'Someone made a purchase';
          break;
        case 'favorite':
          message = 'Added to favorites';
          break;
        case 'mention':
          message = `Mentioned on ${signal.platform || 'social media'}`;
          break;
        default:
          message = 'New activity';
      }

      return {
        message,
        time: timeAgo,
        type: signal.type,
      };
    });
  }

  /**
   * Get time ago string
   */
  private getTimeAgo(timestamp: Date): string {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return `${Math.floor(seconds / 604800)}w ago`;
  }

  /**
   * Calculate FOMO (Fear of Missing Out) indicators
   */
  calculateFOMOIndicators(signals: SocialSignal[]): {
    recentPurchases: number;
    viewingNow: number;
    addedToFavorites: number;
    limitedAvailability: boolean;
  } {
    const now = Date.now();
    const hourAgo = now - 60 * 60 * 1000;

    const recentSignals = signals.filter(s => new Date(s.timestamp).getTime() > hourAgo);

    return {
      recentPurchases: recentSignals.filter(s => s.type === 'purchase').length,
      viewingNow: Math.floor(Math.random() * 15) + 3, // Simulated
      addedToFavorites: recentSignals.filter(s => s.type === 'favorite').length,
      limitedAvailability: Math.random() > 0.7, // Simulated
    };
  }

  /**
   * Generate testimonial highlights
   */
  generateTestimonialHighlights(
    reviews: { content: string; rating: number; author: string; date: Date }[]
  ): { quote: string; author: string; rating: number }[] {
    // Filter to positive reviews with substantial content
    const goodReviews = reviews
      .filter(r => r.rating >= 4 && r.content.length > 50)
      .sort((a, b) => b.rating - a.rating);

    return goodReviews.slice(0, 3).map(review => ({
      quote: review.content.length > 150
        ? review.content.substring(0, 147) + '...'
        : review.content,
      author: review.author,
      rating: review.rating,
    }));
  }

  /**
   * Calculate trust indicators
   */
  calculateTrustIndicators(
    signals: SocialSignal[],
    entityAge: number, // days
    isVerified: boolean
  ): { score: number; indicators: string[] } {
    const indicators: string[] = [];
    let score = 0;

    if (isVerified) {
      indicators.push('Verified Business');
      score += 20;
    }

    if (entityAge > 365) {
      indicators.push('Established Since ' + new Date(Date.now() - entityAge * 24 * 60 * 60 * 1000).getFullYear());
      score += 15;
    }

    const positiveReviews = signals.filter(
      s => s.type === 'review' && s.sentiment === 'positive'
    ).length;

    if (positiveReviews > 20) {
      indicators.push(`${positiveReviews}+ Positive Reviews`);
      score += 25;
    }

    const repeatCustomers = new Set(
      signals.filter(s => s.type === 'purchase' && s.userId).map(s => s.userId)
    ).size;

    if (repeatCustomers > 10) {
      indicators.push('Repeat Customers');
      score += 20;
    }

    return { score: Math.min(100, score), indicators };
  }
}

const socialProofAgent = new SocialProofAgent();
export default socialProofAgent;
